import {useCallback,useEffect,useState} from "react";
import api,{formatApiErrorDetail} from "@/lib/api";
const field="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm";
const btn="inline-flex min-h-10 items-center rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-40";

export default function ExclusiveDelivery(){
 const [reservations,setReservations]=useState([]);
 const [agencies,setAgencies]=useState([]);
 const [buyers,setBuyers]=useState([]);
 const [selected,setSelected]=useState({});
 const [review,setReview]=useState({});
 const [confirmed,setConfirmed]=useState({});
 const [busy,setBusy]=useState("");
 const [error,setError]=useState("");
 const [notice,setNotice]=useState("");
 const load=useCallback(async()=>{
   const [a,b,c]=await Promise.all([
     api.get("/platform/lead-buyers/reservations"),
     api.get("/platform/agencies"),
     api.get("/platform/lead-buyers")
   ]);
   setReservations(a.data||[]);setAgencies(b.data||[]);setBuyers(c.data||[]);
 },[]);
 useEffect(()=>{load().catch(()=>setError("Unable to load exclusive deliveries."));},[load]);
 const deliver=async(item)=>{
   if(busy||!selected[item.id]||!confirmed[item.id]||(review[item.id]||"").trim().length<8)return;
   const buyer=buyers.find(b=>b.id===item.buyer_id);
   const agency=agencies.find(a=>a.id===selected[item.id]);
   if(!window.confirm(`Transfer this prepaid, consent-authorized exclusive inquiry to ${agency?.legal_name||"the selected agency"}? Only the verified named buyer may receive the personal data. Agency marketing automation stays disabled until its own consent review.`))return;
   setBusy(item.id);setError("");setNotice("");
   try{
     const response=await api.post(`/platform/lead-buyers/reservations/${item.id}/deliver-internal`,{
       agency_id:selected[item.id],review_reference:review[item.id],
       buyer_identity_verified:true,consumer_authorization_verified:true,
     });
     setNotice(`Lead ${item.lead_id.slice(0,8)} delivered to the verified internal agency. Agency record ${response.data.agency_lead_id?.slice(0,8)||""} is awaiting independent contact-permission review.`);
     await load();
   }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"Unable to deliver this reserved inquiry.");await load().catch(()=>{});}
   finally{setBusy("");}
 };
 const reconcile=async item=>{
   if(busy||!confirmed[item.id]||(review[item.id]||"").trim().length<8)return;
   if(!window.confirm("Confirm you independently checked the existing agency copy and delivery ledger? This reconciles the existing transfer, creates no new lead copy, and leaves contact disabled."))return;
   setBusy(item.id);setError("");
   try{
     await api.post(`/platform/lead-buyers/reservations/${item.id}/reconcile-delivery`,{
       confirm_existing_copy_verified:true,review_reference:review[item.id]});
     setNotice("Existing delivery reconciled. Consumer contact remains disabled.");
   }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"Reconciliation could not be completed.");}
   finally{await load().catch(()=>{});setBusy("");}
 };
 return <section className="space-y-4 rounded-xl border bg-card p-4 sm:p-6" data-testid="exclusive-delivery">
   <div>
     <h2 className="font-heading text-lg font-semibold">4. Fulfill prepaid exclusive reservations</h2>
     <p className="mt-1 text-xs text-muted-foreground">Delivery into a verified, already-onboarded Fortunate Operations agency tenant. No external email, public link, or automatic outreach. External buyers still require their own secure delivery integration.</p>
   </div>
   {error&&<p role="alert" className="rounded-md border p-3 text-sm text-destructive">{error}</p>}
   {notice&&<p role="status" className="rounded-md border p-3 text-sm">{notice}</p>}
   {!reservations.length&&<p className="text-sm text-muted-foreground">No exclusive lead reservations yet.</p>}
   {reservations.map(r=>{
     const buyer=buyers.find(b=>b.id===r.buyer_id);
     const allowed=agencies.filter(a=>(a.legal_name||"").trim().toLowerCase()===
       (buyer?.legal_name||"").trim().toLowerCase()&&a.active!==false);
     return <article key={r.id} className="space-y-3 rounded-lg border p-3 text-sm" data-testid={`reservation-${r.id}`}>
       <div className="flex flex-wrap justify-between gap-2">
         <p><strong>{buyer?.legal_name||"Buyer"}</strong> · lead {r.lead_id.slice(0,8)}</p>
         <span className="rounded-full border px-2 py-1 text-xs">{r.status}</span>
       </div>
       {r.status==="reserved"&&<>
         {!allowed.length&&<p className="text-xs text-amber-800">No active internal agency has this verified buyer's exact legal name. Do not deliver this record to a different agency.</p>}
         <label className="block text-xs font-semibold">Matched receiving agency
           <select className={field} value={selected[r.id]||""}
             onChange={e=>setSelected(s=>({...s,[r.id]:e.target.value}))}>
             <option value="">Select verified matching agency</option>
             {allowed.map(a=><option value={a.id} key={a.id}>{a.legal_name} · {a.id}</option>)}
           </select>
         </label>
         <label className="block text-xs font-semibold">Independent delivery review reference
           <input className={field} value={review[r.id]||""}
             onChange={e=>setReview(s=>({...s,[r.id]:e.target.value}))}/>
         </label>
         <label className="flex items-start gap-2 text-xs">
           <input type="checkbox" className="mt-1" checked={!!confirmed[r.id]}
             onChange={e=>setConfirmed(s=>({...s,[r.id]:e.target.checked}))}/>
           <span>I independently verified the buyer identity, licensing, paid order, named recipient and the consumer's explicit sharing authorization for this delivery.</span>
         </label>
         <button type="button" className={btn} disabled={Boolean(busy)||!selected[r.id]||!confirmed[r.id]||(review[r.id]||"").length<8}
           onClick={()=>deliver(r)} data-testid="deliver-exclusively">Deliver securely to this agency</button>
       </>}
       {r.status==="delivered"&&<p className="text-xs text-slate-600">Delivered within the platform. The receiving agency may review the lead in its own pipeline; automated contact remains disabled by default.</p>}
       {r.status==="delivery_needs_reconciliation"&&<div className="space-y-2">
         <p>A partial transfer needs review. Do not retry delivery or create another agency copy. Reconciliation requires exactly one existing agency copy with matching buyer and source identities.</p>
         <label className="block">Reconciliation evidence reference<input className={field} value={review[r.id]||""}
           onChange={e=>setReview(s=>({...s,[r.id]:e.target.value}))}/></label>
         <label className="flex items-start gap-2"><input type="checkbox" checked={!!confirmed[r.id]}
           onChange={e=>setConfirmed(s=>({...s,[r.id]:e.target.checked}))}/>I verified the existing agency copy and delivery evidence.</label>
         <button type="button" className={btn} disabled={Boolean(busy)||!confirmed[r.id]||(review[r.id]||"").trim().length<8}
           onClick={()=>reconcile(r)}>Reconcile existing delivery</button>
       </div>}
     </article>;
   })}
 </section>;
}

